import {
  CRITICAL_WORKFLOW_EXPLICIT_PAIN_WINDOW_INSTRUMENTATION_VERSION,
  CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME,
  MAX_PAIN_OBSERVATION_DURATION_MS,
} from './constants';
import type {LoafObservationRuntime} from './loafObservationRuntime';
import type {
  CriticalWorkflowDurationVitalOptions,
  CriticalWorkflowExplicitDatadogContext,
  CriticalWorkflowExplicitObservationHandle,
  CriticalWorkflowExplicitObservationOptions,
  RecordCriticalWorkflowDurationVital,
} from './types';

type LifecycleOutcome = CriticalWorkflowExplicitDatadogContext['outcome'];

interface LifecycleObservation {
  id: number;
  workflow: string;
  workflowContext?: CriticalWorkflowExplicitObservationOptions['workflowContext'];
  startTime: number;
  maxDurationMs: number;
  endTime?: number;
  backstopTimeoutId: number;
  animationFrameId?: number;
  finalizeTimeoutId?: number;
}

export class LifecyclePainObserver {
  private readonly observations = new Map<number, LifecycleObservation>();

  constructor(
    private readonly runtime: LoafObservationRuntime,
    private readonly recordDurationVital: RecordCriticalWorkflowDurationVital
  ) {}

  begin(
    options: CriticalWorkflowExplicitObservationOptions
  ): CriticalWorkflowExplicitObservationHandle | undefined {
    if (!this.isValid(options)) {
      return undefined;
    }
    const started = this.runtime.createCollector();
    if (started == null) {
      return undefined;
    }

    const observation: LifecycleObservation = {
      id: started.collectorId,
      workflow: options.workflow,
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      startTime: started.startTime,
      maxDurationMs: options.maxDurationMs,
      backstopTimeoutId: 0,
    };
    observation.backstopTimeoutId = window.setTimeout(
      () => this.discardObservation(observation),
      options.maxDurationMs
    );
    this.observations.set(observation.id, observation);

    return {
      complete: () => this.completeObservation(observation.id),
      cancel: () => this.cancelObservation(observation.id),
    };
  }

  handlePageAbandonment(): void {
    const abandonTime = performance.now();
    for (const observation of this.observations.values()) {
      if (observation.endTime != null) {
        this.finishObservation(observation.id, 'completed');
        continue;
      }
      // A hidden document is the last reliable moment to seal the interval.
      // requestAnimationFrame does not run while hidden, so drain synchronously.
      const sealed = this.sealObservation(observation, abandonTime);
      if (sealed) {
        this.finishObservation(observation.id, 'abandoned');
      }
    }
  }

  private isValid(options: CriticalWorkflowExplicitObservationOptions) {
    return (
      options.workflow.length > 0 &&
      Number.isFinite(options.maxDurationMs) &&
      options.maxDurationMs > 0 &&
      options.maxDurationMs <= MAX_PAIN_OBSERVATION_DURATION_MS
    );
  }

  private completeObservation(id: number): boolean {
    const observation = this.observations.get(id);
    if (observation == null || observation.endTime != null) {
      return false;
    }
    const sealed = this.sealObservation(observation, performance.now());
    if (!sealed) {
      return false;
    }

    this.runtime.drain();
    observation.animationFrameId = window.requestAnimationFrame(() => {
      observation.animationFrameId = undefined;
      observation.finalizeTimeoutId = window.setTimeout(() => {
        observation.finalizeTimeoutId = undefined;
        this.finishObservation(id, 'completed');
      }, 0);
    });
    return true;
  }

  // Seals the half-open interval at endTime. Discards the observation when the
  // elapsed time reached the maximum or the collector cannot be sealed.
  private sealObservation(
    observation: LifecycleObservation,
    endTime: number
  ): boolean {
    const reachedMaxDuration =
      endTime - observation.startTime >= observation.maxDurationMs;
    if (reachedMaxDuration) {
      this.discardObservation(observation);
      return false;
    }
    const sealed = this.runtime.sealCollector(observation.id, endTime);
    if (!sealed) {
      this.discardObservation(observation);
      return false;
    }
    observation.endTime = endTime;
    window.clearTimeout(observation.backstopTimeoutId);
    return true;
  }

  private cancelObservation(id: number): boolean {
    const observation = this.observations.get(id);
    if (observation == null || observation.endTime != null) {
      return false;
    }
    this.discardObservation(observation);
    return true;
  }

  private finishObservation(id: number, outcome: LifecycleOutcome): void {
    const observation = this.observations.get(id);
    if (observation?.endTime == null) {
      return;
    }
    const duration = Math.max(0, observation.endTime - observation.startTime);
    const metrics = this.runtime.finalizeCollector(observation.id, duration);
    if (metrics == null) {
      this.discardObservation(observation);
      return;
    }

    this.clearObservationResources(observation);
    this.observations.delete(id);
    const vital: CriticalWorkflowDurationVitalOptions = {
      startTime: performance.timeOrigin + observation.startTime,
      duration,
      context: {
        instrumentationVersion:
          CRITICAL_WORKFLOW_EXPLICIT_PAIN_WINDOW_INSTRUMENTATION_VERSION,
        workflow: observation.workflow,
        observationMode: 'explicit',
        outcome,
        maxDurationMs: observation.maxDurationMs,
        ...(observation.workflowContext == null
          ? {}
          : {workflowContext: observation.workflowContext}),
        ...metrics,
      },
    };
    this.recordDurationVital(CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME, vital);
  }

  private discardObservation(observation: LifecycleObservation): void {
    if (!this.observations.has(observation.id)) {
      return;
    }
    this.clearObservationResources(observation);
    this.observations.delete(observation.id);
    this.runtime.cancelCollector(observation.id);
  }

  private clearObservationResources(observation: LifecycleObservation): void {
    window.clearTimeout(observation.backstopTimeoutId);
    if (observation.animationFrameId != null) {
      window.cancelAnimationFrame(observation.animationFrameId);
    }
    if (observation.finalizeTimeoutId != null) {
      window.clearTimeout(observation.finalizeTimeoutId);
    }
  }
}
